import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth, ROLE_META, ALL_VALID_ROLES, type Role } from "../auth/AuthContext";
import { Icon, type IconName } from "../components/Icon";
import { CommandPalette } from "../components/CommandPalette";
import { Copilot } from "../components/Copilot";
import { useTheme } from "../system/ThemeContext";
import { useCopilot } from "../system/CopilotContext";
import { Taskbar } from "./Taskbar";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  roles: Role[];
  badge?: { kind: "warn" | "danger"; count: number };
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

/** Alias local: todos los roles válidos (para ítems visibles a todos). */
const ALL_ROLES: Role[] = ALL_VALID_ROLES;

const NAV: NavGroup[] = [
  {
    title: "Centro de mando",
    items: [
      {
        to: "/",
        label: "Centro Ejecutivo",
        icon: "home",
        roles: ALL_ROLES,
      },
      {
        to: "/alertas",
        label: "Centro de Alertas",
        icon: "bell",
        roles: ["Gerente", "Comercial", "Operador", "Analista_NRW", "AnalistaFugas", "Supervisor", "Admin"],
        badge: { kind: "danger", count: 9 },
      },
      {
        to: "/casos",
        label: "Casos Prioritarios",
        icon: "flask",
        roles: ["Gerente", "Comercial", "Operador", "Analista_NRW", "AnalistaFugas", "Supervisor", "Admin"],
        badge: { kind: "warn", count: 5 },
      },
      {
        to: "/insights",
        label: "Operational Insights",
        icon: "search",
        roles: ["Gerente", "Comercial", "Operador", "Analista_NRW", "AnalistaFugas", "Supervisor", "Admin"],
      },
      {
        to: "/live",
        label: "Live Operations",
        icon: "wifi",
        roles: ALL_ROLES,
      },
      {
        to: "/mapa",
        label: "Mapa Operacional",
        icon: "search",
        roles: ALL_ROLES,
      },
    ],
  },
  {
    title: "Comercial & Cumplimiento",
    items: [
      {
        to: "/comercial",
        label: "Pérdidas Comerciales",
        icon: "report",
        roles: ["Gerente", "Comercial", "Supervisor", "Admin"],
      },
      {
        to: "/fugas",
        label: "Fugas Domiciliarias",
        icon: "droplet",
        roles: ["AnalistaFugas", "Operador", "Supervisor", "Admin"],
      },
      {
        to: "/regulatorio",
        label: "Cumplimiento SISS",
        icon: "shield",
        roles: ["Regulatorio", "Gerente", "Supervisor", "Admin"],
      },
    ],
  },
  {
    title: "Analítica NRW",
    items: [
      {
        to: "/operador/dashboards",
        label: "Dashboards",
        icon: "dashboard",
        roles: ["Gerente", "Operador", "Analista_NRW", "Supervisor", "Admin"],
      },
      {
        to: "/analista/balance",
        label: "Balance & NRW",
        icon: "droplet",
        roles: ["Analista_NRW", "Supervisor", "Admin"],
      },
      {
        to: "/dmas",
        label: "DMAs",
        icon: "users",
        roles: ["Analista_NRW", "Supervisor", "Admin"],
      },
      {
        to: "/impacto",
        label: "Impacto Económico",
        icon: "report",
        roles: ["Gerente", "Analista_NRW", "Supervisor", "Admin"],
      },
      // Oculto del nav del demo: tracker de roadmap, no analítica. Ruta
      // /mvp y <MvpProgram> siguen activas; reactivar si se requiere.
      // {
      //   to: "/mvp",
      //   label: "Programa MVP 90d",
      //   icon: "clock",
      //   roles: ["Gerente", "Operador", "Analista_NRW", "Supervisor", "Admin"],
      // },
      {
        to: "/analista/whatif",
        label: "Simulaciones What-if",
        icon: "flask",
        roles: ["Analista_NRW", "Supervisor", "Admin"],
      },
      {
        to: "/prediccion",
        label: "Predicción",
        icon: "dashboard",
        roles: ["Analista_NRW", "Supervisor", "Admin"],
      },
    ],
  },
  {
    title: "Operación",
    items: [
      {
        to: "/inspector/ordenes",
        label: "Órdenes",
        icon: "wrench",
        roles: ["Inspector", "Supervisor", "Admin"],
        badge: { kind: "warn", count: 12 },
      },
      // Ocultos del nav del demo: páginas placeholder (ModuleScaffold) sin
      // funcionalidad real. Rutas y componentes intactos; reactivar al
      // construir la vista real.
      // {
      //   to: "/terreno",
      //   label: "Terreno",
      //   icon: "droplet",
      //   roles: ["Inspector", "Supervisor", "Admin"],
      // },
      {
        to: "/cuadrillas",
        label: "Cuadrillas",
        icon: "users",
        roles: ["Supervisor", "Admin"],
      },
      // {
      //   to: "/sla",
      //   label: "SLA",
      //   icon: "clock",
      //   roles: ["Gerente", "Supervisor", "Admin"],
      // },
      // {
      //   to: "/evidencias",
      //   label: "Evidencias",
      //   icon: "shield",
      //   roles: ["Inspector", "Supervisor", "Admin", "Regulatorio"],
      // },
    ],
  },
  {
    title: "GIS & Red",
    items: [
      // Oculto del nav del demo: placeholder (ModuleScaffold) sin datos.
      // {
      //   to: "/red",
      //   label: "Red Hidráulica",
      //   icon: "server",
      //   roles: ["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"],
      // },
      {
        to: "/valvulas",
        label: "Válvulas",
        icon: "settings",
        roles: ["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"],
      },
      {
        to: "/presion",
        label: "Presión",
        icon: "droplet",
        roles: ["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"],
      },
      {
        to: "/prvs",
        label: "PRVs",
        icon: "settings",
        roles: ["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"],
      },
      // Ocultos del nav del demo: placeholders (ModuleScaffold) sin datos.
      // {
      //   to: "/cobertura",
      //   label: "Cobertura IoT",
      //   icon: "wifi",
      //   roles: ["IngenieroRF", "Analista_NRW", "Supervisor", "Admin"],
      // },
      {
        to: "/lora",
        label: "LoRa Network",
        icon: "wifi",
        roles: ["IngenieroRF", "Analista_NRW", "Supervisor", "Admin"],
      },
      // {
      //   to: "/heatmaps",
      //   label: "Heatmaps",
      //   icon: "search",
      //   roles: ["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"],
      // },
    ],
  },
  {
    title: "Plataforma",
    items: [
      {
        to: "/calidad",
        label: "Calidad de Datos",
        icon: "server",
        roles: ["Analista_NRW", "Supervisor", "Admin", "Regulatorio"],
      },
      {
        to: "/supervisor/catastro",
        label: "Catastro",
        icon: "users",
        roles: ["Comercial", "Supervisor", "Admin"],
      },
      {
        to: "/supervisor/auditoria",
        label: "Auditoría",
        icon: "shield",
        roles: ["Supervisor", "Admin", "Regulatorio"],
      },
      {
        to: "/supervisor/parametros",
        label: "Parámetros",
        icon: "settings",
        roles: ["Supervisor", "Admin"],
      },
      // Ocultos del nav del demo: placeholders (ModuleScaffold) sin datos.
      // {
      //   to: "/integraciones",
      //   label: "Integraciones",
      //   icon: "server",
      //   roles: ["Supervisor", "Admin"],
      // },
      {
        to: "/observabilidad",
        label: "Observabilidad",
        icon: "wifi",
        roles: ["IngenieroRF", "Analista_NRW", "Supervisor", "Admin"],
      },
      // {
      //   to: "/usuarios",
      //   label: "Usuarios & RBAC",
      //   icon: "users",
      //   roles: ["Supervisor", "Admin"],
      // },
    ],
  },
];

function findActiveLabel(pathname: string): string {
  for (const group of NAV) {
    for (const item of group.items) {
      if (item.to === pathname) return item.label;
    }
  }
  if (pathname === "/") return "Centro Ejecutivo";
  return "Plataforma";
}

/** Grupo que contiene la ruta activa (para auto-expandir). */
function findActiveGroup(pathname: string): string | null {
  for (const group of NAV) {
    for (const item of group.items) {
      if (item.to === pathname) return group.title;
    }
  }
  if (pathname === "/") return "Centro de mando";
  return null;
}

const SIDEBAR_COLLAPSED_KEY = "anc.sidebar.collapsedGroups";

function readCollapsedGroups(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    /* ignore */
  }
  // Por defecto: solo "Centro de mando" abierto, el resto colapsado.
  return new Set(["Analítica NRW", "Operación", "GIS & Red", "Plataforma"]);
}

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps): JSX.Element {
  const { roles, user, logout, isDemo, activeRole, setDemoRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { openCopilot } = useCopilot();
  const navigate = useNavigate();
  const location = useLocation();
  const activeLabel = findActiveLabel(location.pathname);
  const activeGroup = findActiveGroup(location.pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsedGroups);
  const profile = user?.profile as
    | { name?: string; email?: string; preferred_username?: string }
    | undefined;
  const displayName =
    profile?.name ?? profile?.preferred_username ?? profile?.email ?? "Operador";

  // Auto-expandir el grupo de la ruta activa al navegar.
  useEffect(() => {
    if (!activeGroup) return;
    setCollapsed((prev) => {
      if (!prev.has(activeGroup)) return prev;
      const next = new Set(prev);
      next.delete(activeGroup);
      return next;
    });
  }, [activeGroup]);

  // Cerrar el drawer de navegación móvil al cambiar de ruta.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  function toggleGroup(title: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      try {
        window.localStorage.setItem(
          SIDEBAR_COLLAPSED_KEY,
          JSON.stringify(Array.from(next)),
        );
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  // Global ⌘K / Ctrl+K binding for the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className={`app-shell${mobileNavOpen ? " is-nav-open" : ""}`}>
      <button
        type="button"
        className="app-nav-backdrop"
        aria-label="Cerrar navegación"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => setMobileNavOpen(false)}
      />
      <aside className="app-sidebar" aria-label="Navegación principal">
        <div className="app-sidebar__brand">
          <span className="app-sidebar__brand-icon">
            <Icon name="droplet" size={16} />
          </span>
          <span>ANC NRW Platform</span>
        </div>
        {NAV.map((group) => {
          const visible = group.items.filter((item) =>
            item.roles.some((r) => roles.includes(r)),
          );
          if (visible.length === 0) return null;
          const isCollapsed = collapsed.has(group.title);
          const hasActive = group.items.some((i) => i.to === location.pathname);
          // Total de badges del grupo (para mostrar resumen al colapsar).
          const badgeTotal = visible.reduce(
            (acc, i) => acc + (i.badge?.count ?? 0),
            0,
          );
          return (
            <div key={group.title} className="app-sidebar__group">
              <button
                type="button"
                className="app-sidebar__group-toggle"
                aria-expanded={!isCollapsed}
                onClick={() => toggleGroup(group.title)}
              >
                <span
                  className={`app-sidebar__group-chevron ${
                    isCollapsed ? "" : "is-open"
                  }`}
                >
                  <Icon name="chevron-right" size={11} />
                </span>
                <span className="app-sidebar__group-name">{group.title}</span>
                {isCollapsed && badgeTotal > 0 && (
                  <span className="app-sidebar__group-badge">{badgeTotal}</span>
                )}
                {isCollapsed && hasActive && (
                  <span className="app-sidebar__group-dot" aria-hidden="true" />
                )}
              </button>
              {!isCollapsed &&
                visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className="app-sidebar__link"
                    end={item.to === "/"}
                  >
                    <span className="app-sidebar__icon" aria-hidden="true">
                      <Icon name={item.icon} size={14} />
                    </span>
                    <span>{item.label}</span>
                    {item.badge && (
                      <span
                        className={
                          item.badge.kind === "danger"
                            ? "app-sidebar__badge app-sidebar__badge--danger"
                            : "app-sidebar__badge"
                        }
                      >
                        {item.badge.count}
                      </span>
                    )}
                  </NavLink>
                ))}
            </div>
          );
        })}
        <div className="app-sidebar__footer">
          v0.2 · {isDemo ? "demo" : "live"} · build {new Date().getFullYear()}
        </div>
      </aside>

      <header className="app-topbar">
        <button
          type="button"
          className="app-topbar__menu-btn"
          aria-label={mobileNavOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <Icon name="menu" size={18} />
        </button>
        <div className="app-topbar__breadcrumbs">
          ANC <Icon name="chevron-right" size={10} />{" "}
          <strong>{activeLabel}</strong>
        </div>
        <div className="app-topbar__center">
          <button
            type="button"
            className="cmd-trigger"
            onClick={() => setPaletteOpen(true)}
            aria-label="Abrir paleta de comandos"
          >
            <Icon name="search" size={13} />
            <span>Buscar páginas, comandos, alertas…</span>
            <kbd>⌘K</kbd>
          </button>
        </div>
        <div className="app-topbar__user">
          {isDemo && (
            <span className="badge badge--warn" title="Modo demo activo">
              Demo
            </span>
          )}
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => openCopilot()}
            aria-label="Abrir ANC Copilot"
            title="ANC Copilot"
          >
            <Icon name="flask" size={13} /> <span className="btn__label">Copilot</span>
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={toggleTheme}
            aria-label="Alternar tema"
            title={theme === "dark" ? "Tema claro" : "Tema oscuro"}
          >
            <Icon name={theme === "dark" ? "dashboard" : "shield"} size={14} />
          </button>
          {isDemo ? (
            <label className="role-switcher" title="Cambiar de usuario / rol (modo demo)">
              <span className="role-switcher__icon" aria-hidden="true">
                <Icon name="users" size={13} />
              </span>
              <select
                aria-label="Cambiar de usuario"
                value={activeRole ?? ""}
                onChange={(e) => {
                  const next = e.target.value as Role;
                  setDemoRole(next);
                  navigate(ROLE_META[next].home);
                }}
              >
                {ALL_VALID_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_META[r].label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span style={{ fontSize: 12.5 }}>
              <strong>{displayName}</strong>
              <span className="text-muted">
                {" "}
                · {activeRole ? ROLE_META[activeRole].label : "—"}
              </span>
            </span>
          )}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void logout()}
          >
            <Icon name="logout" size={13} /> <span className="btn__label">Salir</span>
          </button>
        </div>
      </header>

      <main className="app-main fade-in">{children}</main>

      <Taskbar />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <Copilot />
    </div>
  );
}
